// Ported from deexoptions-mobile-app/components/SurveyComps/SurveyQuestionView.tsx.

import React from "react";
import { OTHER_OPTION_ID, type SurveyQuestion as Question } from "../../hooks/queries/useSurvey";

/**
 * One survey question, in the shape its type implies. The parent owns the answer so it can
 * save on advance; this component is presentational plus local validation affordances.
 *
 * `value` follows the API's per-type contract: option id (SINGLE_CHOICE, or OTHER_OPTION_ID),
 * array of ids (MULTI_CHOICE), string (text), number (NPS/RATING).
 */
export type AnswerValue = string | string[] | number | null;

type Props = {
  question: Question;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  /** Free text captured when "Other" is picked. */
  otherText: string;
  onOtherTextChange: (text: string) => void;
  /** Suggestion ids the user clicked, reported back with text answers. */
  usedSuggestions: string[];
  onUsedSuggestionsChange: (ids: string[]) => void;
};

const Choice = ({
  label,
  selected,
  onClick,
  rounded,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  rounded: "full" | "md";
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`mb-2.5 flex w-full items-center justify-between border px-4 py-3.5 text-left ${
      rounded === "full" ? "rounded-full" : "rounded-xl"
    } ${
      selected
        ? "border-secondary_button bg-[#D4EBFD] dark:bg-secondary_button/20"
        : "border-gray-200 dark:border-gray-700"
    }`}
  >
    <span
      className={`flex-1 pr-2 text-[15px] ${
        selected
          ? "font-semibold text-secondary_button"
          : "text-gray-600 dark:text-gray-200"
      }`}
    >
      {label}
    </span>
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
        selected
          ? "border-secondary_button"
          : "border-gray-300 dark:border-gray-600"
      }`}
    >
      {selected && (
        <span className="h-2.5 w-2.5 rounded-full bg-secondary_button" />
      )}
    </span>
  </button>
);

const SurveyQuestion: React.FC<Props> = ({
  question,
  value,
  onChange,
  otherText,
  onOtherTextChange,
  usedSuggestions,
  onUsedSuggestionsChange,
}) => {
  const type = question.type;

  /* ----------------------------- choice types ----------------------------- */
  if (type === "SINGLE_CHOICE" || type === "MULTI_CHOICE") {
    const isMulti = type === "MULTI_CHOICE";
    const selected: string[] = isMulti
      ? Array.isArray(value)
        ? value
        : []
      : typeof value === "string" && value
      ? [value]
      : [];

    const limit = question.maxSelections ?? null;
    const atLimit = isMulti && limit !== null && selected.length >= limit;

    const toggle = (id: string) => {
      if (!isMulti) {
        onChange(id);
        return;
      }
      if (selected.includes(id)) {
        onChange(selected.filter((x) => x !== id));
        return;
      }
      // Silently refusing a click reads as a broken button, so the cap is also surfaced
      // as helper text above.
      if (atLimit) return;
      onChange([...selected, id]);
    };

    const options = question.options ?? [];
    const showOther = Boolean(question.allowOther);
    const otherSelected = selected.includes(OTHER_OPTION_ID);

    return (
      <div>
        {isMulti && (
          <p className="mb-3 text-[13px] text-gray-400">
            {limit ? `Choose up to ${limit}` : "Choose all that apply"}
          </p>
        )}
        {options.map((o) => (
          <Choice
            key={o.id}
            label={o.label}
            selected={selected.includes(o.id)}
            onClick={() => toggle(o.id)}
            rounded={isMulti ? "md" : "full"}
          />
        ))}
        {showOther && (
          <>
            <Choice
              label="Other"
              selected={otherSelected}
              onClick={() => toggle(OTHER_OPTION_ID)}
              rounded={isMulti ? "md" : "full"}
            />
            {otherSelected && (
              <input
                value={otherText}
                onChange={(e) => onOtherTextChange(e.target.value)}
                placeholder={question.otherPlaceholder ?? "Tell us more"}
                className="mb-2.5 w-full rounded-xl border border-gray-200 bg-transparent px-4 py-3.5 text-[15px] text-black outline-none dark:border-gray-700 dark:text-white"
              />
            )}
          </>
        )}
      </div>
    );
  }

  /* ------------------------------ text types ------------------------------ */
  if (type === "SHORT_TEXT" || type === "LONG_TEXT") {
    const isLong = type === "LONG_TEXT";
    const text = typeof value === "string" ? value : "";
    const max = question.maxLength ?? (isLong ? 2000 : 200);
    const suggestions = question.suggestions ?? [];

    // Chips append to what is already typed rather than replacing it — they are starting
    // points, not a fixed option list.
    const appendSuggestion = (id: string, label: string) => {
      const next = text.trim() ? `${text.trim()} ${label}` : label;
      onChange(next.slice(0, max));
      if (!usedSuggestions.includes(id)) {
        onUsedSuggestionsChange([...usedSuggestions, id]);
      }
    };

    const inputClass =
      "w-full rounded-xl border border-gray-200 bg-transparent px-4 py-3.5 text-[15px] text-black outline-none dark:border-gray-700 dark:text-white";

    return (
      <div>
        {isLong ? (
          <textarea
            value={text}
            onChange={(e) => onChange(e.target.value.slice(0, max))}
            placeholder={question.placeholder ?? "Type your answer..."}
            maxLength={max}
            rows={5}
            className={`${inputClass} resize-none`}
          />
        ) : (
          <input
            value={text}
            onChange={(e) => onChange(e.target.value.slice(0, max))}
            placeholder={question.placeholder ?? "Type your answer..."}
            maxLength={max}
            className={inputClass}
          />
        )}
        <p className="mt-1.5 text-right text-[12px] text-gray-400">
          {text.length}/{max}
        </p>

        {suggestions.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[13px] text-gray-400">
              Or start from one of these
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => appendSuggestion(s.id, s.label)}
                  className="rounded-full border border-secondary_button px-3.5 py-2 text-[13px] font-semibold text-secondary_button"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ----------------------------- scale types ------------------------------ */
  if (type === "NPS" || type === "RATING") {
    const min = question.scale?.min ?? (type === "NPS" ? 0 : 1);
    const max = question.scale?.max ?? (type === "NPS" ? 10 : 5);
    const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    const picked = typeof value === "number" ? value : null;
    const isNps = type === "NPS";
    // Stretching only holds up for a short scale; an admin who builds a 1-10 RATING gets
    // the scrolling treatment instead of ten cramped cells. Kept at <= 7 even on desktop
    // widths so the mobile web layout stays correct.
    const stretch = !isNps && points.length <= 7;

    const cells = points.map((n) => {
      const on = picked === n;
      return (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`flex items-center justify-center border text-[16px] font-semibold ${
            stretch
              ? "h-14 flex-1 rounded-2xl"
              : isNps
              ? "h-12 w-12 shrink-0 rounded-xl"
              : "h-14 w-14 shrink-0 rounded-2xl"
          } ${
            on
              ? "border-secondary_button bg-secondary_button text-white"
              : "border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-200"
          }`}
        >
          {n}
        </button>
      );
    });

    return (
      <div>
        {stretch ? (
          // A rating fills the row so its cells line up with the min/max labels below.
          // `gap-2` puts space between cells with no trailing gap, which is what keeps the
          // last cell flush with the right edge and the maxLabel under it.
          <div className="flex w-full gap-2">{cells}</div>
        ) : (
          // NPS is 11 wide: fixed cells that scroll, because eleven stretched cells on a
          // phone gives targets too small to hit.
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {cells}
          </div>
        )}
        {(question.scale?.minLabel || question.scale?.maxLabel) && (
          // Each label owns half the row, so they sit under the first and last cell rather
          // than at whatever width the text happens to be.
          <div className="mt-1.5 flex">
            <span className="flex-1 text-[12px] text-gray-400">
              {question.scale?.minLabel ?? ""}
            </span>
            <span className="flex-1 text-right text-[12px] text-gray-400">
              {question.scale?.maxLabel ?? ""}
            </span>
          </div>
        )}
      </div>
    );
  }

  // An unknown type from a newer backend should not blank the screen.
  return (
    <p className="text-[14px] text-gray-400">
      This question type isn&apos;t supported in this version of the app.
    </p>
  );
};

export default SurveyQuestion;
